import { none, useHookstate } from '@hookstate/core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import { simpleGetter } from '../../../actions/common';
import { getClassStudentsFromExcel } from '../../../actions/course';
import { classesApi, courseApi, lessonsApi, universalApi, userApi } from '../../../api/api';
import ArrowDown from '../../../assets/icons/ArrowDown';
import ArrowLeft from '../../../assets/icons/ArrowLeft';
import ArrowLeftWithBody from '../../../assets/icons/ArrowLeftWithBody';
import CheckMark from '../../../assets/icons/CheckMark';
import Cross from '../../../assets/icons/Cross';
import Delete from '../../../assets/icons/Delete';
import Edit from '../../../assets/icons/Edit';
import Plus from '../../../assets/icons/Plus';
import Tick from '../../../assets/icons/Tick';
import TrashRed from '../../../assets/icons/TrashRed';
import UploadFile from '../../../assets/icons/UploadFile';
import fileUploadGray from '../../../assets/svg/file-upload-gray.svg';
import listAddwhite from '../../../assets/svg/list-add-white.svg';
import { Avatar } from '../../../components/Avatar';
import { CustomDateInput } from '../../../components/CustomDateInput';
import Modal from '../../../components/Modal/Modal';
import DefaultButton from '../../../components/default-button/DefaultButton';
import InputField from '../../../components/input-field/InputField';
import { useRooms } from '../../../hooks/cabinet';
import { useLesson, useMyCourses } from '../../../hooks/course';
import { useUsersWithSocialScoreAndDemerits } from '../../../hooks/demerit-typed';
import {
  useMainOrganizationSearch,
  useMainOrganizations,
} from '../../../hooks/main-data';
import { useDebounce, useOutsideClick } from '../../../hooks/ui';
import { courseButtons, getStudentBgColor, lessonActions, lessonReportExcel, timeSlots } from '../../../misc/constants';
import { exportToExcelTemplate, getAverageRatingForLesson, getDateOnly, getStudentAttendanceAndGrade, showSnackbar } from '../../../misc/utils';
import { useGlobalMiscState } from '../../../stores/misc';
import { useGlobalUserState } from '../../../stores/user';
import { LessonAdditionalMaterial } from './LessonAdditionalMaterial';
import ScheduleOfWeek from '../../../components/Course/ScheduleOfWeek';
import { format } from 'date-fns';
import SearchableSelect from '../../../components/input-field/SearchableSelect';

const attendanceSelectOptions = [
  {
    label: 'Нет',
    value: 0
  },
  {
    label: 'Да',
    value: 1
  },
]

const LessonSingleMediaAction = ({ lesson, mediaKey, onSuccess }) => {
  const onFileChange = async (e) => {
    if (e.target.files?.length) {
      const formData = new FormData();
      formData.append('type', 'single')
      formData.append('media_key', `${mediaKey}_media_id`)
      formData.append('lesson_id', lesson.id)
      formData.append('file', e.target.files[0])
      const response = await simpleGetter(lessonsApi.uploadMedia, formData)
      if (response.success) {
        showSnackbar('Успешно', 'success')
        onSuccess()
      }
    }
  }

  const deleteMedia = async () => {
    const body = {
      lesson_id: lesson.id,
      media_key: `${mediaKey}_media_id`,
      action: 'delete'
    }
    const response = await simpleGetter(lessonsApi.uploadMedia, body)
    if (response.success) {
      showSnackbar('Успешно', 'success')
      onSuccess()
    }
  }

  const renderUploadButton = text => {
    return (
      <label>
        <div className='flex flex-row gap-1 cursor-pointer items-center border border-valley-main text-valley-main py-1 px-3 rounded-lg'>
          <UploadFile />
          <span className='font-semibold text-[13px] leading-[120%]'>
            {text}
          </span>
        </div>
        <input type='file' hidden onChange={onFileChange} />
      </label>
    )
  }

  if (!lesson[`${mediaKey}_media_id`]) {
    return renderUploadButton('Загрузить')
  }

  return (
    <div className='flex flex-row items-center gap-6'>
      <a className='font-medium text-[15px] leading-[130%] text-valley-main' href={lesson[`${mediaKey}_media`].link} target='_blank' rel='noreferrer'>
        1 файл
      </a>
      <div className='flex flex-row gap-2'>
        {renderUploadButton('Заменить')}
        <button onClick={deleteMedia} className='flex flex-row gap-1 cursor-pointer items-center border border-valley-red text-valley-red py-1 px-3 rounded-lg'>
          <TrashRed />
          <span className='font-semibold text-[13px] leading-[120%]'>
            Удалить
          </span>
        </button>
      </div>
    </div>
  )
}

export const Courses = () => {
  const usersListRef = useRef(null);
  const navigate = useNavigate();
  const userState = useGlobalUserState();
  const miscState = useGlobalMiscState();
  const classSelectorRef = useRef(null);
  const user = userState.get();
  const addCourseModalOpen = Boolean(miscState.get('addCourseModalOpen'));
  const addClassModalOpen = Boolean(miscState.get('addClassModalOpen'));
  const localState = useHookstate({
    page: 0,
    attendancesAndGrades: {},
    createLessonForm: {
      room_id: '',
      description: '',
      start_date: null,
      start_time: '',
      end_time: ''
    },
  });
  const state = localState.get();
  const courseSelected = localState.courseSelected?.get() || {};
  const searchMode = localState.searchMode?.get() || 'search';
  const classSelected = localState.classSelected?.get() || {};
  const studentsSearch = localState.studentsSearch?.get() || '';
  const classSelectOpen = Boolean(localState.classSelectOpen?.get());
  const selectedOrganization = localState.selectedOrganization?.get() || {};
  const allStudentsNotInCourseTriggerFetch = state.allStudentsNotInCourseTriggerFetch || 0;
  const lessonTriggerFetch = state.lessonTriggerFetch || 0;
  const page = state.page;
  const viewMode = state.viewMode || "courses";
  const isCoursesViewMode = viewMode === "courses";
  const isLessonsViewMode = viewMode === "lessons";
  const selectedLesson = state.selectedLesson || {};
  const selectedStudents = localState.selectedStudents?.get() || [];
  const attendancesAndGrades = state.attendancesAndGrades;
  const additionalMaterialModalOpen = Boolean(state.additionalMaterialModalOpen);
  const updateDescriptionModalOpen = Boolean(state.updateDescriptionModalOpen);
  const createLessonModalOpen = Boolean(state.createLessonModalOpen);
  const createLessonForm = state.createLessonForm;
  const currentLessonDescription = state.currentLessonDescription || "";
  const isGradesEdit = Boolean(state.isGradesEdit)
  const debouncedQuery = useDebounce(studentsSearch, 500);
  const organizations = useMainOrganizations();
  const rooms = useRooms('', undefined, false);
  const lesson = useLesson(selectedLesson.id, lessonTriggerFetch);
  const [searchResults, searchLoading] = useMainOrganizationSearch(
    selectedOrganization.id,
    debouncedQuery,
    localState.resetSearchResults?.get() || 0
  );
  useOutsideClick(
    classSelectorRef,
    () => localState.merge({ classSelectOpen: false }),
    ['class-select-option', 'class-select-option-child', 'class-add-button', 'class-add-button-child']
  );

  const updateCreateLessonForm = (key) => (e) => {
    localState.createLessonForm[key].set(e.target.value)
  }

  const onCreateLessonDateChange = (date) => {
    localState.createLessonForm.start_date.set(date);
  }

  const shouldPreventFetch = searchMode === "search"

  const postUpdateStudentsNotInCourse = newList => {
    localState.usersList.set(newList);
  }

  const [allStudentsNotInCourse, allStudentsLoading, allStudentsTotal] =
    useUsersWithSocialScoreAndDemerits(allStudentsNotInCourseTriggerFetch, {
      page,
      type: 'classes',
      usersWhere: {
        query: debouncedQuery
      },
      classesWhere: {
        course_id: courseSelected.id
      }
    },
      postUpdateStudentsNotInCourse,
      shouldPreventFetch
    )

  useEffect(() => {
    localState.merge({
      loading: allStudentsLoading,
      total: allStudentsTotal
    })
  }, [allStudentsLoading, allStudentsTotal])

  const handleScroll = useCallback(() => {
    if (usersListRef.current) {
      const stateLocal = localState.get()
      const usersList = stateLocal.usersList || []
      const total = stateLocal.total || 0
      const loading = stateLocal.loading || false
      const { scrollTop, scrollHeight, clientHeight } = usersListRef.current;
      // console.log(scrollHeight, scrollTop, clientHeight * 1, scrollHeight - scrollTop <= clientHeight * 1, usersList.length, total, loading, usersList.length < total && !loading)
      if (scrollHeight - scrollTop <= clientHeight * 1 && usersList.length < total && !loading) {
        localState.merge({
          page: (stateLocal.page || 0) + 1
        })
      }
    }
  }, [])

  useEffect(() => {
    if (courseSelected.id && usersListRef.current) {
      usersListRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (usersListRef.current) {
        usersListRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [courseSelected.id])

  const selectClass = cl => () => {
    localState.merge({
      classSelected: cl.id ? cl : {},
      classSelectOpen: false
    })
  }

  const updateLocalValue = (key, value) => () => {
    localState.merge({
      [key]: value,
    });
  };

  const postCoursesFetch = (newList) => {
    if (courseSelected.id) {
      const found = newList.find((item) => item.id === courseSelected.id);
      if (found) {
        updateLocalValue('courseSelected', found)();
        if (classSelected.id) {
          const foundClass = found.classes?.find(
            (c) => c.id === classSelected.id
          );
          if (foundClass) {
            updateLocalValue('classSelected', foundClass)();
          }
        }
      }
    }
  };

  useEffect(() => {
    updateLocalValue('classSelected', {})();
    localState.allStudentsNotInCourseTriggerFetch.set(allStudentsNotInCourseTriggerFetch + 1)
  }, [courseSelected.id]);

  useEffect(() => {
    updateLocalValue('studentsSearch', '')();
    updateLocalValue(
      'resetSearchResults',
      (localState.resetSearchResults?.get() || 0) + 1
    )();
    updateLocalValue('selectedStudents', [])();
  }, [searchMode]);

  useEffect(() => {
    if (organizations.length) {
      updateLocalValue('selectedOrganization', organizations[0])();
    }
  }, [organizations]);

  const triggerFetch = () => {
    localState.merge({
      coursesTriggerFetch: (localState.coursesTriggerFetch?.get() || 0) + 1,
    });
  };

  const triggerStudentsNotInClassFetch = () => {
    localState.allStudentsNotInCourseTriggerFetch.set(allStudentsNotInCourseTriggerFetch + 1)
  }

  const onSelectStudent = (s) => () => {
    const index = selectedStudents.findIndex((student) => student.id === s.id);
    if (index >= 0) {
      localState.selectedStudents.merge({ [index]: none });
    } else {
      const func = localState.selectedStudents?.get()?.length ? 'merge' : 'set';
      localState.selectedStudents[func]([s]);
    }
  };

  const courses = useMyCourses(localState.coursesTriggerFetch?.get() || 0, postCoursesFetch)

  const lessonsGrouped = (courseSelected.classes
    ?.reduce((acc, curr) => [...acc, ...(curr.lessons || [])], [])
    ?.sort((a, b) => new Date(b.start_time) - new Date(a.start_time))) || []

  // Преобразование всех уроков из всех курсов в формат для расписания
  const allLessonsForSchedule = useMemo(() => {
    const scheduleLessons = [];
    let lessonIdCounter = 1;

    courses.forEach((course) => {
      course.classes?.forEach((classItem) => {
        classItem.lessons?.forEach((lesson) => {
          const lessonDate = new Date(lesson.start_time);
          const room = rooms.find((r) => r.id === lesson.room_id);
          const roomName = room?.name || '';
          
          // Получаем имя учителя, если есть
          let teacherName = '';
          if (lesson.teacher_data) {
            teacherName = `${lesson.teacher_data.firstname || ''} ${lesson.teacher_data.lastname || ''}`.trim();
          } else if (lesson.class?.teacher_data) {
            teacherName = `${lesson.class.teacher_data.firstname || ''} ${lesson.class.teacher_data.lastname || ''}`.trim();
          }

          scheduleLessons.push({
            id: lessonIdCounter++,
            courseCode: course.name || '',
            subjectName: classItem.name || lesson.title || '',
            startTime: format(lessonDate, 'HH:mm'),
            endTime: format(new Date(lesson.end_time), 'HH:mm'),
            day: lessonDate,
            cabinet: roomName,
            teacher: teacherName,
            lessonId: lesson.id,
            courseId: course.id,
            classId: classItem.id,
          });
        });
      });
    });

    return scheduleLessons;
  }, [courses, rooms]);

  // Обработчик клика на урок в расписании
  const handleScheduleLessonClick = useCallback((scheduleLesson) => {
    if (!scheduleLesson.lessonId || !scheduleLesson.courseId || !scheduleLesson.classId) {
      return;
    }

    // Находим курс
    const foundCourse = courses.find((c) => c.id === scheduleLesson.courseId);
    if (!foundCourse) return;

    // Находим класс
    const foundClass = foundCourse.classes?.find((c) => c.id === scheduleLesson.classId);
    if (!foundClass) return;

    // Находим урок
    const foundLesson = foundClass.lessons?.find((l) => l.id === scheduleLesson.lessonId);
    if (!foundLesson) return;

    // Устанавливаем курс, класс и урок
    localState.merge({
      courseSelected: foundCourse,
      classSelected: foundClass,
      selectedLesson: foundLesson,
      viewMode: 'lessons',
    });
  }, [courses, localState]);

  const setModalVisible = (key) => () => {
    miscState.set(key, !Boolean(miscState.get(key)));
  };

  const onLocalStateChangeNative = (key) => (e) => {
    localState.merge({
      [key]: e.target.value,
    });
  };

  const onCourseAdd = async () => {
    const response = await simpleGetter(courseApi.create, {
      name: localState.newCourseName?.get()
    })
    if (response.success) {
      triggerFetch();
      setModalVisible('addCourseModalOpen')();
      updateLocalValue('newCourseName', '')();
    }
  };

  const onClassAdd = async () => {
    const response = await simpleGetter(universalApi.create, {
      model: 'Class',
      body: {
        create: {
          course_id: courseSelected.id,
          name: localState.newClassName?.get(),
        },
      },
    });
    if (response.item) {
      triggerFetch();
      setModalVisible('addClassModalOpen')();
      updateLocalValue('newClassName', '')();
      showSnackbar('Успешно', 'success');
    }
  };

  const onStudentsUpload = async (e) => {
    const list = await getClassStudentsFromExcel(e);
    if (list.length) {
      const response = await simpleGetter(userApi.getUsersByExternalIds, {
        ids: list,
      });
      if (response.users?.length) {
        const addResponse = await simpleGetter(classesApi.uploadStudentsList, {
          class_id: classSelected.id,
          list: response.users.map((user) => {
            const { id, ...rest } = user;
            rest.main_id = id;
            return rest;
          }),
        });
        if (addResponse.success) {
          triggerFetch();
          showSnackbar('Успешно', 'success');
        }
      }
    }
  };

  const onOrganizationSelect = (e) => {
    const foundOrganization = organizations.find(
      (org) => org.id === parseInt(e.target.value)
    );
    if (foundOrganization) {
      updateLocalValue('selectedOrganization', foundOrganization)();
    }
  };

  const addStudentToClass = (student) => async () => {
    const addResponse = await simpleGetter(courseApi.addStudentsListToClass, {
      class_id: classSelected.id,
      list: [student].map(user => user.id),
    });
    if (addResponse.success) {
      triggerFetch();
      updateLocalValue('searchMode', 'search')();
      showSnackbar('Успешно', 'success');
    }
  };

  const addSelectedStudentToClass = async () => {
    const addResponse = await simpleGetter(courseApi.addStudentsListToClass, {
      class_id: classSelected.id,
      list: selectedStudents.map(user => user.id),
    });
    if (addResponse.success) {
      triggerFetch();
      updateLocalValue('searchMode', 'search')();
      showSnackbar('Успешно', 'success');
      updateLocalValue('selectedStudents', [])();
    }
  };

  const selectLesson = (lesson) => () => {
    localState.merge({
      viewMode: 'lessons',
      selectedLesson: lesson
    })
  }

  const triggerLessonFetch = () => {
    localState.lessonTriggerFetch.set(lessonTriggerFetch + 1)
  }

  const toggleCreateLessonModal = () => {
    localState.createLessonModalOpen.set(!createLessonModalOpen);
  }

  const toggleAdditionalMaterialModal = () => {
    localState.additionalMaterialModalOpen.set(!additionalMaterialModalOpen)
  }

  const toggleUpdateDescriptionModal = () => {
    if (!updateDescriptionModalOpen) {
      localState.currentLessonDescription.set(lesson.description || "")
    }
    localState.updateDescriptionModalOpen.set(!updateDescriptionModalOpen)
  }

  const renderLessonActionButton = key => {
    switch (key) {
      case 'homework':
      case 'test':
        return <LessonSingleMediaAction lesson={lesson} mediaKey={key} onSuccess={triggerLessonFetch} />
      default:
        if (lesson.additional_media?.length) {
          return (
            <div className='flex flex-row items-center gap-6'>
              <span className='font-medium text-[15px] leading-[130%] text-valley-main'>
                {lesson.additional_media?.length} файлов
              </span>
              <button onClick={toggleAdditionalMaterialModal} className='bg-valley-background-blue p-0.5 rounded-md'>
                <Edit additionalStyle='text-valley-gray' />
              </button>
            </div>
          )
        }
        return (
          <DefaultButton
            Icon={UploadFile}
            additionalStyle='gap-1 items-center border border-valley-main text-valley-main py-1 px-3 rounded-lg font-semibold text-[13px] leading-[120%]'
            buttonText='Открыть'
            onClick={toggleAdditionalMaterialModal}
          />
        )
    }
  }

  const renderLessonAction = (lessonAction) => {
    const Icon = lessonAction.Icon
    return (
      <div className='col-span-1 flex flex-row items-center justify-between px-4'>
        <div className='flex flex-row items-center gap-2'>
          <Icon color="#AFB7CA" />
          <span className='font-medium text-[15px] leading-[130%] text-valley-black'>
            {lessonAction.text}
          </span>
        </div>
        {renderLessonActionButton(lessonAction.key)}
      </div>
    )
  }

  const isAddCourseDisabled = !localState.newCourseName?.get()?.length;
  const isAddClassDisabled = !localState.newClassName?.get()?.length;
  const classSelectOptions = [
    {
      name: 'Все классы',
      studentsDisplayLength: courseSelected?.classes?.reduce(
        (acc, curr) => acc + curr.students?.length,
        0
      ),
      type: 'default',
    },
    ...(courseSelected?.classes?.map((c) => ({
      ...c,
      studentsDisplayLength: c.students?.length,
      type: 'id',
    })) || []),
  ];

  let studentsToRender = [];
  if (searchMode === 'search') {
    if (classSelected.id) {
      studentsToRender = classSelected.students?.map((s) => ({
        ...s,
        class: classSelected,
      }));
    } else {
      studentsToRender = courseSelected.classes?.reduce(
        (acc, curr) => [
          ...acc,
          ...curr.students?.map((s) => ({ ...s, class: curr, totalGrade: curr.lessons.reduce((acc, curr) => acc + (curr.attendances.find(a => a.main_user_id === s.id)?.grade || 0), 0) })),
        ],
        []
      );
    }
    if (studentsSearch.length) {
      studentsToRender = studentsToRender.filter((s) => {
        return (
          s.firstname?.toLowerCase()?.includes(studentsSearch.toLowerCase()) ||
          s.lastname?.toLowerCase()?.includes(studentsSearch.toLowerCase()) ||
          s.externalId?.toLowerCase()?.includes(studentsSearch.toLowerCase()) ||
          s.email?.toLowerCase()?.includes(studentsSearch.toLowerCase())
        );
      });
    }
  } else {
    studentsToRender = allStudentsNotInCourse;
  }

  const onGradeChange = (s, key) => e => {
    if (localState.attendancesAndGrades[s.id]) {
      localState.attendancesAndGrades[s.id]?.[key]?.set?.(e.target.value)
    } else {
      localState.attendancesAndGrades[s.id].set({
        [key]: e.target.value
      })
    }
  }

  const onCourseBackButtonClick = () => {
    localState.merge({
      courseSelected: {},
      classSelected: {},
      selectedLesson: {},
      viewMode: 'courses',
    })
  }

  const onSubmitGrades = async () => {
    const finalObject = {}
    for (const k of Object.keys(attendancesAndGrades)) {
      if (!isNaN(parseInt(attendancesAndGrades[k].did_attend)) && !isNaN(parseInt(attendancesAndGrades[k].grade))) {
        finalObject[k] = attendancesAndGrades[k];
      }
    }
    const response = await simpleGetter(lessonsApi.updateGrades, {
      lesson_id: lesson.id,
      attendancesAndGrades: finalObject
    })
    if (response.success) {
      triggerLessonFetch()
      toggleIsGradesEdit(false)()
      showSnackbar('Успешно', 'success')
    }
  }

  const onDescriptionUpdate = async () => {
    const response = await simpleGetter(lessonsApi.updateDescription, {
      lesson_id: lesson.id,
      description: currentLessonDescription
    })
    if (response.success) {
      triggerLessonFetch()
      toggleUpdateDescriptionModal()
      showSnackbar('Успешно', 'success')
    }
  }

  const downloadReport = lesson => async () => {
    const foundClass = courseSelected.classes.find(c => c.id === lesson.class_id)
    if (foundClass) {
      const finalData = JSON.parse(JSON.stringify(foundClass.students))
      for (const s of finalData) {
        const foundAttendance = lesson.attendances.find(a => a.main_user_id === s.id)
        if (foundAttendance) {
          if (foundAttendance.did_attend) {
            s.did_attend = true
          }
          if (typeof foundAttendance.grade === 'number') {
            s.grade = foundAttendance.grade
          }
        }
      }
      await exportToExcelTemplate(`lesson_${getDateOnly(lesson.start_time).replaceAll('.', '_')}_report`, finalData, lessonReportExcel, courseSelected, lesson)()
    }
  }

  const switchToCoursesViewMode = () => {
    localState.merge({
      viewMode: 'courses'
    })
  }

  const toggleIsGradesEdit = (setValues = false) => () => {
    if (setValues) {
      const object = {}
      for (const student of lesson.class.students) {
        object[student.id] = {
          did_attend: 0,
          grade: '-'
        }
        const found = lesson.attendances.find(a => a.main_user_id === student.id)
        if (found && found.grade) {
          object[student.id] = {
            did_attend: found.did_attend,
            grade: String(found.grade)
          }
        }
      }
      localState.attendancesAndGrades.set(object)
    }
    localState.isGradesEdit.set(!isGradesEdit)
  }

  const submitCreateLesson = async () => {
    const response = await simpleGetter(lessonsApi.create, {
      main_user_id: courseSelected.main_user_id,
      class_id: classSelected.id,
      room_id: createLessonForm.room_id,
      title: classSelected?.name || '',
      description: createLessonForm.description,
      start_time: new Date(`${createLessonForm.start_date.toDateString()} ${createLessonForm.start_time}`),
      end_time: new Date(`${createLessonForm.start_date.toDateString()} ${createLessonForm.end_time}`),
      is_attendance_ongoing: 0
    })

    if (response.success) {
      localState.createLessonForm.set({
        room_id: '',
        description: '',
        start_date: null,
        start_time: '',
        end_time: ''
      })
      triggerFetch()
      toggleCreateLessonModal();
      showSnackbar('Успешно', 'success')
    }
  }

  const startLessonAttendance = (lesson) => async () => {
    const response = await simpleGetter(lessonsApi.updateIsAttendanceOngoing, {
      lesson_id: lesson.id,
      is_attendance_ongoing: 1
    })

    if (response.success) {
      triggerFetch()
      showSnackbar('Успешно', 'success')
    }
  }

  const renderAttendance = (lesson) => {
    const now = new Date();
    if (now < new Date(lesson.start_time)) {
      return null
    }

    if (now > new Date(lesson.end_time)) {
      return (
        <span className='text-[13px] leading-[15.6px] text-valley-gray'>
          Аттенданс завершён
        </span>
      )
    }

    return lesson.is_attendance_ongoing ? (
      <span className='text-[13px] leading-[15.6px] text-valley-main'>
        Аттенданс идёт
      </span>
    ) : (
      <DefaultButton
        additionalStyle={`border border-valley-dark-green text-valley-dark-green rounded-lg`}
        buttonText='Начать аттенданс'
        onClick={startLessonAttendance(lesson)}
      />
    )
  }

  useEffect(() => {
    if (state.courseSelected?.id) {
      console.log('Курс выбран:', state.courseSelected.name);
    } else {
      console.log('Курс не выбран');
    }    
  }, [localState.courseSelected?.get()])

  return (
    <div className="w-full h-full flex-col bg-[#f8f9fd] p-4">
      <div className="flex  items-start gap-4 mb-4">
        {isLessonsViewMode && (
          <button onClick={switchToCoursesViewMode} className='bg-white flex flex-row justify-center items-center rounded-full w-[50px] h-[50px] shrink-0'>
            <ArrowLeft additionalStyle='text-valley-light-gray' />
          </button>
        )}
        <div className="w-full md:w-1/2">
          <div className="bg-white py-2 px-2 rounded-xl flex flex-row items-center">
            <span onClick={onCourseBackButtonClick} className="font-medium text-sm text-[#5F6B88] mr-2 cursor-pointer p-2 shrink-0">
              Курс
            </span>
            <div className="flex flex-row overflow-x-auto flex-grow mr-2">
              {[...courses]
                .sort((a, b) => b.id - a.id)
                .map((c) => (
                  <button
                    className={`flex flex-row items-center justify-center p-2 mr-1 lg:mr-2 rounded-lg ${courseSelected.id === c.id ? 'bg-[#0068FF]' : ''
                      }`}
                    onClick={updateLocalValue('courseSelected', c)}
                  >
                    <span
                      className={`${courseSelected.id === c.id
                        ? 'text-white'
                        : 'text-[#1A1A1A]'
                        } font-medium text-sm line-clamp-2`}
                    >
                      {c.name}
                    </span>
                  </button>
                ))}
            </div>
            <DefaultButton
              additionalStyle="bg-gray-100 text-gray-500 rounded-lg px-4"
              iconStyle='text-valley-main'
              onClick={setModalVisible('addCourseModalOpen')}
              Icon={Plus}
            />
          </div>
        </div>
      </div>
      {
        !state.courseSelected?.id && (
          <div>
            <ScheduleOfWeek 
              lessons={allLessonsForSchedule}
              onLessonClick={handleScheduleLessonClick}
            />
          </div>
        )
      }
      {(Boolean(courseSelected.id) || !isCoursesViewMode) && (
        <div className="flex row flex-1 gap-4 overflow-y-auto">
          {isCoursesViewMode && (
            <div className="flex-1 flex flex-col bg-white rounded-xl p-3">
              <div className="flex flex-col items-start justify-between pt-[9px] pb-[17px] gap-4">
                <div className="flex flex-col flex-1 items-start justify-between gap-4 w-full">
                  <p className="text-xl">
                    <span className="font-semibold text-[#1A1A1A]">
                      Студенты на курсе
                    </span>
                    <span className="ml-0.5 text-[#AFB7CA]">
                      (
                      {courseSelected.classes?.reduce(
                        (acc, curr) => acc + curr.students?.length,
                        0
                      )}
                      )
                    </span>
                  </p>
                  <div className="relative flex flex-row w-full">
                    <div
                      onClick={updateLocalValue('classSelectOpen', true)}
                      ref={classSelectorRef}
                      className="cursor-pointer flex flex-row rounded-lg bg-[#F0F3FA] w-full py-[9px] px-3"
                    >
                      <p className="text-[15px] flex-1">
                        <span className="text-[#1A1A1A]">
                          {classSelected.id ? classSelected.name : 'Все классы'}
                        </span>
                        <span className="ml-0.5 text-[#AFB7CA]">
                          (
                          {classSelected.id
                            ? classSelected.students?.length
                            : courseSelected.classes?.reduce(
                              (acc, curr) => acc + curr.students?.length,
                              0
                            )}
                          )
                        </span>
                      </p>
                      <ArrowDown additionalStyle="ml-0.5 text-gray-500" />
                    </div>
                    {classSelectOpen && (
                      <div className="absolute top-[42px] bg-white class-select-shadow flex flex-col w-full rounded-xl py-2 z-20">
                        {classSelectOptions.map((c) => {
                          const isSelected =
                            c.type === 'default'
                              ? !classSelected.id
                              : classSelected.id === c.id;
                          return (
                            <div
                              onClick={selectClass(c)}
                              className={`flex-1 flex flex-row cursor-pointer class-select-option p-3 ${isSelected ? 'bg-[#0068FF]' : ''
                                }`}
                            >
                              <p className="text-[15px] class-select-option-child">
                                <span
                                  className={`${isSelected ? 'text-white' : 'text-[#1A1A1A]'} class-select-option-child`}
                                >
                                  {c.name}
                                </span>
                                <span
                                  className={`ml-0.5 class-select-option-child ${isSelected
                                    ? 'text-[#FFFFFF80]'
                                    : 'text-[#AFB7CA]'
                                    }`}
                                >
                                  ({c.studentsDisplayLength})
                                </span>
                              </p>
                            </div>
                          );
                        })}
                        <button
                          onClick={() => { setModalVisible('addClassModalOpen')(); localState.merge({ classSelectOpen: false }) }}
                          className="m-3 border border-[#0068FF] flex flex-row justify-center items-center py-2 rounded-lg class-add-button"
                        >
                          <Plus additionalStyle="text-blue-500 class-add-button-child" />
                          <span className="ml-2 text-[15px] text-[#0068FF] font-medium class-add-button-child">
                            Создать новый класс
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 flex flex-row justify-start w-full">
                  {Boolean(classSelected.id) && (
                    <label className="cursor-pointer rounded-lg border border-[#AFB7CA] py-1.5 px-3 flex flex-row items-center w-full justify-center">
                      <img alt="file-upload-gray" src={fileUploadGray} />
                      <span className="ml-2 font-bold text-[13px] text-[#5F6B88] whitespace-nowrap">
                        Загрузить список студентов
                      </span>
                      <input hidden type="file" onChange={onStudentsUpload} />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  className="admin-input-width-auto w-full"
                  placeholder="Поиск"
                  value={localState.studentsSearch?.get() || ''}
                  onChange={onLocalStateChangeNative('studentsSearch')}
                />
                <div className="flex flex-row w-full rounded-lg p-1 bg-[#0068FF12] border border-[#0068FF33]">
                  {courseButtons.map((button) => (
                    <div
                      key={button.key}
                      className={`${button.key === searchMode
                        ? 'bg-[#0068FF] text-white'
                        : 'bg-transparent text-blue-500'
                        } flex-1 flex flex-row py-1 px-3 rounded-[6px] cursor-pointer items-center justify-center`}
                      onClick={updateLocalValue('searchMode', button.key)}
                    >
                      <DefaultButton
                        Icon={button.imageDefault}
                        additionalStyle={`px-1 border-none`}
                      />

                      <span className={` ml-1 text-[15px] leading-[18px]`}>
                        {button.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {searchMode === 'add' && (
                <div className="flex flex-row mt-4">
                  <select
                    className="admin-input-width-auto w-full"
                    value={selectedOrganization.id}
                    onChange={onOrganizationSelect}
                  >
                    <option>Выберите организацию</option>
                    {organizations.map((org) => (
                      <option value={org.id}>{org.title}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex flex-col flex-1 mt-3 overflow-y-auto" ref={usersListRef}>
                <table className="table-auto students-table">
                  <thead>
                    <tr className="font-medium text-sm text-[#5F6B88]">
                      <th>
                        <input type="checkbox" />
                      </th>
                      <th>
                        <span>№</span>
                      </th>
                      <th>
                        <span>Студент</span>
                      </th>
                      {searchMode === 'search' && (
                        <th>
                          <span>Класс</span>
                        </th>
                      )}
                      <th>
                        <span>Общий балл</span>
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentsToRender.map((s, i) => {
                      const isSelected = selectedStudents.find(
                        (student) => student.id === s.id
                      );
                      return (
                        <tr className="border-y border-[#F0F3FA]">
                          <td>
                            <div className="flex flex-row">
                              <input
                                checked={isSelected}
                                onChange={onSelectStudent(s)}
                                type="checkbox"
                              />
                            </div>
                          </td>
                          <td>
                            <span className="font-medium text-sm text-[#5F6B88]">
                              {i + 1}
                            </span>
                          </td>
                          <td>
                            <div className="flex flex-row items-center">
                              <div
                                className={`min-w-[38px] min-h-[38px] flex flex-row justify-center items-center rounded-full bg-student-${getStudentBgColor(
                                  s.json_saved_data.firstname?.length || 0
                                )}`}
                              >
                                <span className="font-semibold text-[15px] text-white">
                                  {s.json_saved_data.firstname?.at(0)}
                                  {s.json_saved_data.lastname?.at(0)}
                                </span>
                              </div>
                              <span className="ml-3 font-medium text-[15px] text-[#1A1A1A]">
                                {s.json_saved_data.firstname} {s.json_saved_data.lastname}
                              </span>
                            </div>
                          </td>
                          {searchMode === 'search' && (
                            <td>
                              <span className="text-[15px] text-[#5F6B88]">
                                {s.class?.name}
                              </span>
                            </td>
                          )}
                          <td>
                            <span className="font-medium text-[15px] text-[#5F6B88]">
                              {s.totalGrade}
                            </span>
                          </td>
                          <td>
                            {searchMode === 'search' ? (
                              <DefaultButton
                                Icon={Delete}
                                additionalStyle="text-red-400 bg-red-50 px-2 border-none"
                              />
                            ) : (
                              <div className="flex flex-row justify-end">
                                <button
                                  className={`rounded-lg flex flex-row items-center justify-center bg-[#3BBC30] border py-1.5 px-3 ${!classSelected.id ? 'opacity-30' : ''
                                    }`}
                                  onClick={addStudentToClass(s)}
                                  disabled={!classSelected.id}
                                >
                                  <Plus additionalStyle="text-white" />
                                  <span className="ml-2 font-bold text-[15px] text-white">
                                    Добавить в класс
                                  </span>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Boolean(selectedStudents.length) && (
                <button
                  className={`rounded-lg flex flex-row items-center justify-center bg-[#3BBC30] border py-1.5 px-3 ${!classSelected.id ? 'opacity-30' : ''
                    }`}
                  onClick={addSelectedStudentToClass}
                  disabled={!classSelected.id}
                >
                  <img alt="list-add-white" src={listAddwhite} />
                  <span className="ml-2 font-bold text-[15px] text-white">
                    Добавить выбранных студентов в класс
                  </span>
                </button>
              )}
            </div>
          )}
          <div className={`${isCoursesViewMode ? 'flex-1' : 'flex-1'} flex flex-col gap-3 bg-white rounded-xl p-3`}>
            <div className='flex flex-row justify-between items-center gap-2'>
              <span className="text-xl font-semibold leading-[110%] text-valley-black">
                Посещаемость класса
              </span>
              {classSelected.id && (
                <DefaultButton
                  additionalStyle={`border border-valley-dark-green text-valley-dark-green py-1 px-3 rounded-lg`}
                  buttonText='Создать урок'
                  onClick={toggleCreateLessonModal}
                  Icon={Plus}
                />
              )}
            </div>
            <div className="flex flex-col flex-1 mt-3 overflow-y-auto">
              <table className="table-auto attendance-table">
                <thead>
                  <tr className="font-medium text-sm text-[#5F6B88] border-b border-valley-background-blue">
                    <th className='text-left'>
                      <span>Дата</span>
                    </th>
                    <th className='text-center'>
                      <span>Средний балл</span>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lessonsGrouped.map(l => (
                    <tr className={`border-b border-valley-background-blue ${l.id === selectedLesson.id && isLessonsViewMode ? 'bg-valley-main text-white' : 'text-valley-black'}`}>
                      <td className='rounded-l-lg'>
                        <span className='font-medium text-[15px] leading-[130%]'>
                          {getDateOnly(l.start_time)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='font-medium text-[15px] leading-[130%]'>
                          {getAverageRatingForLesson(l)}
                        </span>
                      </td>
                      <td className='rounded-r-lg'>
                        <div className='flex flex-col gap-2 py-3'>
                          {renderAttendance(l)}
                          <DefaultButton
                            additionalStyle={`${l.id === selectedLesson.id && isLessonsViewMode ? 'border-white text-white' : 'border-valley-main text-valley-main'} border py-1 px-3 rounded-lg`}
                            buttonText='Скачать отчёт'
                            onClick={downloadReport(l)}
                          />
                          {isCoursesViewMode && (
                            <DefaultButton
                              additionalStyle='border border-valley-gray text-valley-gray py-1 px-3 rounded-lg'
                              buttonText='Список студентов'
                              onClick={selectLesson(l)}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {!isCoursesViewMode && (
            <div className='flex-1 flex flex-col gap-[15px]'>
              
              {/* <div className='grid grid-cols-2 gap-[15px]'>
                {lessonActions.map(renderLessonAction)}
                <div className='col-span-1 flex flex-row gap-4'>
                  {isGradesEdit && (
                    <DefaultButton
                      additionalStyle='flex-1 gap-2 rounded-lg border-valley-red text-valley-red'
                      buttonText='Отменить'
                      Icon={Cross}
                      onClick={updateLocalValue('isGradesEdit', false)}
                      iconStyle='text-valley-red'
                    />
                  )}
                </div>
              </div> */}
              <div className='flex flex-col md:flex-row items-start md:items-center p-4 gap-4 md:gap-6 bg-white rounded-lg'>
                <div className='flex flex-col flex-1 md:flex-[56] gap-2 w-full'>
                  <div className='flex flex-row gap-2 items-center'>
                    <span className='text-[15px] leading-[19.5px] font-bold text-valley-black'>
                      Описание
                    </span>
                    <button onClick={toggleUpdateDescriptionModal} className='bg-valley-background-blue p-0.5 rounded-md'>
                      <Edit additionalStyle='text-valley-gray' />
                    </button>
                  </div>
                  <span className='text-[13px] leading-[16.9px] text-valley-gray'>
                    {lesson.description}
                  </span>
                </div>
                <div className='flex flex-col flex-1 md:flex-[44] gap-3 w-full'>
                  {lessonActions.map(renderLessonAction)}
                </div>
              </div>
              <div className='flex flex-col flex-1 gap-4 bg-white rounded-lg p-3 '>
                <div className='flex flex-col md:flex-row gap-4 md:gap-6'>
                  <input
                    className="admin-input-width-auto w-full"
                    placeholder="Поиск"
                    value={state.lessonStudentsSearch || ''}
                    onChange={onLocalStateChangeNative('lessonStudentsSearch')}
                  />
                  <DefaultButton
                    additionalStyle={`flex-1 gap-2 rounded-lg ${isGradesEdit ? 'bg-valley-dark-green text-white' : 'border border-valley-gray text-valley-gray'}`}
                    buttonText={isGradesEdit ? 'Сохранить' : 'Редактировать оценки и присутствие'}
                    Icon={isGradesEdit ? Tick : Edit}
                    onClick={isGradesEdit ? onSubmitGrades : toggleIsGradesEdit(true)}
                    iconStyle={isGradesEdit ? 'text-white' : 'text-valley-gray'}
                  />
                </div>
                <table className="table-auto lesson-students-table">
                  <thead>
                    <tr className="font-medium text-sm text-[#5F6B88] border-b border-valley-background-blue">
                      <th>
                        <span>№</span>
                      </th>
                      <th>
                        <span>Студент</span>
                      </th>
                      <th>
                        <span>ID</span>
                      </th>
                      <th className='text-center'>
                        <span>Присутствие</span>
                      </th>
                      <th>
                        <span>Балл за урок</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lesson.class?.students?.map((s, i) => {
                      const attendanceAndGrade = getStudentAttendanceAndGrade(lesson, s)
                      return (
                        <tr className='border-b border-valley-background-blue'>
                          <td>
                            <span className='font-medium text-sm leading-[110%] text-valley-gray'>{i + 1}</span>
                          </td>
                          <td>
                            <div className='flex flex-row items-center'>
                              <div className='flex flex-row items-center gap-3'>
                                <Avatar user={s} />
                                <span className='font-medium text-[15px] leading-[19.5px] text-valley-black'>
                                  {s.json_saved_data.firstname} {s.json_saved_data.lastname}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className='font-medium text-[15px] leading-[130%] text-valley-gray'>{s.json_saved_data.externalId}</span>
                          </td>
                          {/* <td>
                            <span className={`font-medium text-[15px] leading-[130%] ${attendanceAndGrade.attendance ? 'text-valley-green' : 'text-valley-red'}`}>
                              {attendanceAndGrade.attendance ? 'Да' : 'Нет'}
                            </span>
                          </td> */}
                          <td>
                            {isGradesEdit ? (
                              <InputField
                                wrapperClassName='w-[120px]'
                                additionalClassName='rounded-lg py-3 px-4 bg-valley-background-blue'
                                type='select'
                                options={attendanceSelectOptions}
                                value={attendancesAndGrades[s.id].did_attend}
                                handleChange={onGradeChange(s, 'did_attend')}
                              />
                            ) : (
                              <span className={`font-medium text-[15px] leading-[130%] ${attendanceAndGrade.attendance ? 'text-valley-green' : 'text-valley-red'}`}>
                                {attendanceAndGrade.attendance ? 'Да' : 'Нет'}
                              </span>
                            )}
                          </td>
                          <td>
                            {isGradesEdit ? (
                              <InputField
                                wrapperClassName='w-[120px]'
                                additionalClassName='rounded-lg py-3 px-4 bg-valley-background-blue'
                                type='text'
                                value={attendancesAndGrades[s.id].grade}
                                handleChange={onGradeChange(s, 'grade')}
                              />
                            ) : (
                              <span className='font-medium text-[15px] leading-[130%] text-valley-black'>{attendanceAndGrade.grade}</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      <Modal
        additionalClassName='!w-[32.5%] h-[50%] rounded-xl flex flex-col p-8'
        visible={additionalMaterialModalOpen}
        setVisible={toggleAdditionalMaterialModal}
      >
        <LessonAdditionalMaterial onSuccess={triggerLessonFetch} lesson={lesson} onClose={toggleAdditionalMaterialModal} />
      </Modal>
      <Modal
        visible={addCourseModalOpen}
        setVisible={setModalVisible('addCourseModalOpen')}
        dimensions="w-full h-1/3 rounded-xl"
      >
        <div className="flex flex-col p-2">
          <div className="flex flex-col px-4 md:px-6">
            <span className="text-xl text-[#1A1A1A] mb-6">Название курса</span>
            <span className="font-medium text-sm text-[#5F6B88] mb-2">
              Курс
            </span>
            <input
              className="admin-input mb-8"
              value={localState.newCourseName?.get() || ''}
              onChange={onLocalStateChangeNative('newCourseName')}
            />
            <div className="flex flex-col gap-3">
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#5F6B88] border py-3`}
                onClick={setModalVisible('addCourseModalOpen')}
              >
                <ArrowLeftWithBody additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Вернуться
                </span>
              </button>
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#3BBC30] border py-3 ${isAddCourseDisabled ? 'opacity-30' : ''
                  }`}
                onClick={onCourseAdd}
                disabled={isAddCourseDisabled}
              >
                <CheckMark additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Создать класс
                </span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        visible={addClassModalOpen}
        setVisible={setModalVisible('addClassModalOpen')}
        dimensions="w-full h-1/3 rounded-xl"
      >
        <div className="flex flex-col p-2">
          <div className="flex flex-row justify-end">
            <button onClick={setModalVisible('addClassModalOpen')}>
              <Cross additionalStyle="text-gray-500" />
            </button>
          </div>
          <div className="flex flex-col px-4">
            <span className="text-xl text-[#1A1A1A] mb-6 md:mb-8">
              Название класса
            </span>
            <span className="font-medium text-sm text-[#5F6B88] mb-2">
              Класс
            </span>
            <input
              className="admin-input mb-8"
              value={localState.newClassName?.get() || ''}
              onChange={onLocalStateChangeNative('newClassName')}
            />
            <div className="flex flex-col md:flex-row gap-3">
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#5F6B88] border py-3`}
                onClick={setModalVisible('addClassModalOpen')}
              >
                <DefaultButton additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Вернуться
                </span>
              </button>
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#3BBC30] border py-3 ${isAddClassDisabled ? 'opacity-30' : ''
                  }`}
                onClick={onClassAdd}
                disabled={isAddClassDisabled}
              >
                <CheckMark additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Создать класс
                </span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        visible={updateDescriptionModalOpen}
        setVisible={setModalVisible('updateDescriptionModalOpen')}
        dimensions="w-full h-1/3 rounded-xl"
      >
        <div className="flex flex-col p-2">
          <div className="flex flex-col px-4">
            <span className="text-xl  text-[#1A1A1A] mb-6">Описание</span>
            <span className="font-medium text-sm text-[#5F6B88] mb-2">
              Курс
            </span>
            <textarea
              value={currentLessonDescription}
              onChange={onLocalStateChangeNative('currentLessonDescription')}
              className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-4 py-3 mt-2 mb-4 text-black"
            />
            <div className="flex flex-col gap-3">
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#5F6B88] border py-3`}
                onClick={setModalVisible('updateDescriptionModalOpen')}
              >
                <ArrowLeftWithBody additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Вернуться
                </span>
              </button>
              <button
                className={`rounded-lg flex-1 flex flex-row items-center justify-center bg-[#3BBC30] border py-3 ${!currentLessonDescription.length ? 'opacity-30' : ''
                  }`}
                onClick={onDescriptionUpdate}
                disabled={!currentLessonDescription.length}
              >
                <CheckMark additionalStyle="text-white" />
                <span className="ml-2 font-bold text-[15px] text-white">
                  Сохранить
                </span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        visible={createLessonModalOpen}
        setVisible={toggleCreateLessonModal}
        additionalClassName="w-full rounded-xl"
      >
        <div className="flex flex-col p-2">
          <div className="flex flex-col px-4 ">
            <span className="text-xl  text-[#1A1A1A] mb-6 ">Создание урока</span>
            <div className='flex flex-col gap-6 md:gap-8'>
              <div className='flex flex-col'>
                <SearchableSelect
                  id="room-select"
                  label="Кабинет"
                  options={rooms.map((room) => ({
                    value: String(room.id),
                    label: room.name
                  }))}
                  value={String(createLessonForm.room_id || '')}
                  handleChange={updateCreateLessonForm('room_id')}
                  additionalClassName="bg-valley-background-blue"
                />
              </div>
              <div className='flex flex-col'>
                <span className="font-medium text-sm text-[#5F6B88] mb-2">
                  Описание урока
                </span>
                <textarea
                  value={createLessonForm.description}
                  onChange={updateCreateLessonForm('description')}
                  className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-4 py-3 mt-2 mb-4 text-black"
                  rows={4}
                />
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col flex-1 text-valley-black gap-2">
                  <p className="text-[#5F6B88] font-medium text-sm">Дата</p>
                  <DatePicker
                    selected={createLessonForm.start_date}
                    onChange={onCreateLessonDateChange}
                    customInput={<CustomDateInput hasTime={false} />}
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <div className="flex flex-col flex-1 gap-2">
                  <InputField
                    wrapperClassName="flex flex-col gap-2"
                    additionalClassName="px-3 py-[10.5px] bg-valley-background-blue"
                    type="select"
                    id={"start-time"}
                    label="Начало"
                    value={createLessonForm.start_time}
                    handleChange={updateCreateLessonForm("start_time")}
                    options={timeSlots.map(t => ({ label: t, value: t }))}
                  />
                </div>
                <div className="flex flex-col flex-1 gap-2">
                  <InputField
                    wrapperClassName="flex flex-col gap-2"
                    additionalClassName="px-3 py-[10.5px] bg-valley-background-blue"
                    type="select"
                    id={"end-time"}
                    label="Конец"
                    value={createLessonForm.end_time}
                    handleChange={updateCreateLessonForm("end_time")}
                    options={timeSlots.map(t => ({ label: t, value: t }))}
                  />
                </div>
              </div>
              <div className='flex flex-col gap-3'>
                <DefaultButton
                  Icon={ArrowLeft}
                  buttonText='Вернуться'
                  additionalStyle='flex-1 bg-valley-gray text-white py-3'
                  onClick={toggleCreateLessonModal}
                />
                <DefaultButton
                  Icon={Tick}
                  buttonText='Создать урок'
                  additionalStyle='flex-1 bg-valley-main text-white py-3'
                  onClick={submitCreateLesson}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
